import { IllustrationAgent, IllustrationPerson, IllustrationRequest, IllustrationRider } from './IllustrationRequest';
import { IllustrationRequestProperty } from '../../../common/js/customer/IllustrationRequestProperty';
import { Utils as CommonUtils, Utils } from '../../../common/js/Utils';
import { Policy } from '../../../common/js/customer/Policy';
import { amorphicStatic } from '@haventech/amorphic';
import { PolicyDoc } from '../../../common/js/customer/PolicyDoc';
import { HtmlPdfController } from './basicIllustration/pdf/HtmlPdfController';
import { IllustrationService } from './IllustrationService';
import moment from 'moment';
import { PolicyDocAction } from '../../../common/js/customer/PolicyDocAction';
import { POLICY_DOC_ACTION_TYPE } from '../../../common/js/static/PolicyDocActionType';
import { Party } from '../../../common/js/customer/Party';
import { Admin } from '../../../common/js/Admin';
import { CoverpathUtils } from '../../js/static/CoverpathUtils';
import { CoverpathPolicy } from '../coverpath_subclasses/CoverpathPolicy';
import { RiderType } from '../../../common/js/static/Product';
import { POLICY_DOC_DOCTYPE, POLICY_DOC_SOURCES, POLICY_DOC_TYPES } from '../../../common/js/constants/policy-doc';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../../../common/js/logger/UILoggerSingleton';

export class RevisedBasicIllustrationUtils {
  static serializeIllustrationRequest(illustrationRequest: IllustrationRequest): Array<IllustrationRequestProperty> {
    const serializedProperties: Array<IllustrationRequestProperty> = [];

    const illustrationRequestKeys = Object.keys(illustrationRequest);
    for (let i = 0; i < illustrationRequestKeys.length; i++) {
      const key = illustrationRequestKeys[i];
      const value = illustrationRequest[key];

      if (!RevisedBasicIllustrationUtils.relevantToRBIConsideration(key)) {
        continue;
      }

      if (
        value instanceof Date ||
        typeof value === 'number' ||
        typeof value === 'string' ||
        typeof value === 'boolean'
      ) {
        serializedProperties.push(RevisedBasicIllustrationUtils.serializeSimpleValue(key, value));
      } else if (key === 'riders') {
        RevisedBasicIllustrationUtils.serializeRiders(value, serializedProperties);
      } else if (key === 'customerPerson') {
        RevisedBasicIllustrationUtils.serializeCustomer(value, serializedProperties);
      } else if (key === 'agent') {
        RevisedBasicIllustrationUtils.serializeAgent(value, serializedProperties);
      } else {
        throw Error(
          `serializeIllustrationRequest failed because of unexpected complex object with a key and typeof ${key}|${typeof value}`
        );
      }
    }

    return serializedProperties;
  }

  static relevantToRBIConsideration(illustrationRequestKey: string) {
    switch (illustrationRequestKey) {
      case 'requestId':
        return false;
      default:
        return true;
    }
  }

  static serializeSimpleValue(key: string, value: Date | number | string | boolean): IllustrationRequestProperty {
    const irp = new IllustrationRequestProperty();
    irp.property = key;

    if (value instanceof Date) {
      irp.dateValue = value;
    } else if (typeof value === 'boolean') {
      irp.booleanValue = value;
    } else if (typeof value === 'number') {
      irp.numberValue = Utils.round(value, 2);
    } else if (typeof value === 'string') {
      irp.stringValue = value;
    }

    return irp;
  }

  static serializeRiders(
    riders: Array<IllustrationRider>,
    serializedProperties: Array<IllustrationRequestProperty>
  ): void {
    riders.forEach((rider) => {
      const riderType = rider.type;
      serializedProperties.push(
        RevisedBasicIllustrationUtils.serializeSimpleValue(`rider-${riderType}-amount`, rider.amount)
      );
      serializedProperties.push(
        RevisedBasicIllustrationUtils.serializeSimpleValue(`rider-${riderType}-premium`, rider.premium)
      );
      serializedProperties.push(
        RevisedBasicIllustrationUtils.serializeSimpleValue(`rider-${riderType}-waiver`, rider.waiver)
      );
      if (riderType === RiderType.ALIR) {
        serializedProperties.push(
          RevisedBasicIllustrationUtils.serializeSimpleValue(`rider-${riderType}-plannedYears`, rider.plannedYears)
        );
      } else if (riderType === RiderType.CIR) {
        serializedProperties.push(
          RevisedBasicIllustrationUtils.serializeSimpleValue(
            `rider-${riderType}-accelerationPercent`,
            rider.accelerationPercent
          )
        );
        serializedProperties.push(
          RevisedBasicIllustrationUtils.serializeSimpleValue(
            `rider-${riderType}-monthlyIncreaseOption`,
            rider.monthlyIncreaseOption
          )
        );
        serializedProperties.push(
          RevisedBasicIllustrationUtils.serializeSimpleValue(
            `rider-${riderType}-puaAddedToBenefitPool`,
            rider.puaAddedToBenefitPool
          )
        );
        serializedProperties.push(
          RevisedBasicIllustrationUtils.serializeSimpleValue(
            `rider-${riderType}-predictedBenefitAge`,
            rider.predictedBenefitAge
          )
        );
      }
    });
  }

  static serializeCustomer(
    customer: IllustrationPerson,
    serializedProperties: Array<IllustrationRequestProperty>
  ): void {
    serializedProperties.push(
      RevisedBasicIllustrationUtils.serializeSimpleValue('customerPerson-firstName', customer.firstName)
    );
    serializedProperties.push(
      RevisedBasicIllustrationUtils.serializeSimpleValue('customerPerson-lastName', customer.lastName)
    );
  }

  static serializeAgent(agent: IllustrationAgent, serializedProperties: Array<IllustrationRequestProperty>): void {
    serializedProperties.push(RevisedBasicIllustrationUtils.serializeSimpleValue('agent-firstName', agent.firstName));
    serializedProperties.push(RevisedBasicIllustrationUtils.serializeSimpleValue('agent-lastName', agent.lastName));

    serializedProperties.push(RevisedBasicIllustrationUtils.serializeSimpleValue('agent-zip', agent.address.zip));
    serializedProperties.push(RevisedBasicIllustrationUtils.serializeSimpleValue('agent-street', agent.address.street));
    serializedProperties.push(RevisedBasicIllustrationUtils.serializeSimpleValue('agent-state', agent.address.state));
    serializedProperties.push(RevisedBasicIllustrationUtils.serializeSimpleValue('agent-city', agent.address.city));
  }

  static findPropertiesWithDifferences(
    previousProps: Array<IllustrationRequestProperty>,
    currentProps: Array<IllustrationRequestProperty>,
    dob
  ): string[] {
    function arePropertiesTheSame(a: IllustrationRequestProperty, b: IllustrationRequestProperty): boolean {
      const aDateIsTruthy = !!a.dateValue;
      const bDateIsTruthy = !!b.dateValue;

      const bothDatesAreTruthy = aDateIsTruthy && bDateIsTruthy;
      const bothDatesAreFalsey = !aDateIsTruthy && !bDateIsTruthy;

      const bothDatesAreTruthyAndTheSame = bothDatesAreTruthy && moment(a.dateValue).isSame(moment(b.dateValue), 'day');

      //Different startDate between BI & RBI can affect insurance age and a new RBI will be required
      let isInsuranceAgeUnChangedIfPropIsStartDate = false;
      if (a.property === 'startDate' && b.property === 'startDate' && bothDatesAreTruthy) {
        isInsuranceAgeUnChangedIfPropIsStartDate = RevisedBasicIllustrationUtils.isInsuranceAgeUnChanged(
          a.dateValue,
          b.dateValue,
          dob
        );
      }

      //Dividends are changing on 11/2/2020. If new asOfDate >= 11/2/2020  and old asOfDate < 11/2/2020 then we need a new RBI
      let asOfDateCausingDividendChange = false;
      if (a.property === 'asOfDate' && b.property === 'asOfDate' && bothDatesAreTruthy) {
        asOfDateCausingDividendChange = RevisedBasicIllustrationUtils.isNewAsOfDateCausingDividendChange(
          a.dateValue,
          b.dateValue
        );
        return !asOfDateCausingDividendChange;
      }

      return (
        //use == because we want null to be considered equal to undefined
        //It's ok to use here because we are comparing objects with typed fields
        // tslint:disable:triple-equals
        a.property == b.property &&
        a.stringValue == b.stringValue &&
        a.numberValue == b.numberValue &&
        a.booleanValue == b.booleanValue &&
        (bothDatesAreFalsey || bothDatesAreTruthyAndTheSame || isInsuranceAgeUnChangedIfPropIsStartDate)
        // tslint:enable:triple-equals
      );
    }

    const propertiesWithDifferences: { [key: string]: boolean } = {};

    //go through all the properties in the previous illustration request and make sure they are present and the same in the current illustration request
    previousProps.forEach((previous) => {
      const exactMatches = currentProps.filter((current) => arePropertiesTheSame(current, previous));

      if (exactMatches.length !== 1) {
        propertiesWithDifferences[previous.property] = true;
      }
    });

    //check for the case where the current illustration request may have more properties than what is in previous
    currentProps.forEach((current) => {
      const exactMatches = previousProps.filter((previous) => arePropertiesTheSame(previous, current));

      if (exactMatches.length !== 1) {
        propertiesWithDifferences[current.property] = true;
      }
    });

    return Object.keys(propertiesWithDifferences);
  }

  static async isPolicyEligibleForAutomaticRBI(policy) {
    const isWholeLifePolicy = policy instanceof CoverpathPolicy && policy.isWholeLifePolicy();

    if (!isWholeLifePolicy) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'RevisedBasicIllustrationUtils',
          function: 'isPolicyEligibleForAutomaticRBI',
          data: {
            policyNumber: policy.policyNumber,
          },
          category: UICategory.request,
        },
        `Policy not eligible for automatic Revised Basic Illustration generation`
      );

      return false;
    }

    const existingRBIs = await PolicyDoc.persistorFetchByQuery({
      policyId: policy._id,
      docType: POLICY_DOC_DOCTYPE.RevisedBasicIllustration,
      isActive: 'true',
    });

    if (existingRBIs.length) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'RevisedBasicIllustrationUtils',
          function: 'isPolicyEligibleForAutomaticRBI',
          data: {
            ...CommonUtils.extractIds(policy),
            policyNumber: policy.policyNumber,
          },
          category: UICategory.request,
        },
        `Policy already has a revised basic illustration; will skip`
      );

      return false;
    }

    const originalProps = await IllustrationRequestProperty.persistorFetchByQuery({
      policy_id: policy._id,
      illustrationType: 'originalBasicIllustration',
    });

    if (originalProps.length === 0) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'RevisedBasicIllustrationUtils',
          function: 'isPolicyEligibleForAutomaticRBI',
          data: {
            policyNumber: policy.policyNumber,
          },
          category: UICategory.request,
        },
        `Policy did not have persisted properties from original Basic Illustration; won't consider for automatic Revised Basic Illustration generation `
      );

      return false;
    }
    return true;
  }

  // If the only differences are productPremiumVersion ignore it
  static requestsAreTheSame(differences) {
    return differences.length === 0 || differences.every((elem) => elem === 'productPremiumVersion');
  }

  static shouldGenerateRBIBasedonParametersChange(originalProps, currentProps, policy) {
    const differences: string[] = RevisedBasicIllustrationUtils.findPropertiesWithDifferences(
      originalProps,
      currentProps,
      policy.insured.person.dob
    );

    const requestsAreTheSame = this.requestsAreTheSame(differences);
    const willGenerateRevisedBasicIllustration = !requestsAreTheSame;

    UILoggerSingleton.getInstance().info(
      {
        module: 'RevisedBasicIllustrationUtils',
        function: 'shouldGenerateRBIBasedonParametersChange',
        context: {
          piiLevel: UIPiiLevelEnum.s2,
        },
        data: {
          policyNumber: policy.policyNumber,
          differences,
          willGenerateRevisedBasicIllustration,
        },
        category: UICategory.request,
      },
      `Results from comparing original and current illustration request`
    );

    return willGenerateRevisedBasicIllustration;
  }

  static async generateNewRBI(primaryPolicy, currentRequest): Promise<PolicyDoc> {
    const rawContent = await HtmlPdfController.new().generateBasicIllustrationPDF(currentRequest, true);
    const illustration64 = rawContent.toString('base64');

    const newRBI = PolicyDoc.new(
      POLICY_DOC_DOCTYPE.RevisedBasicIllustration,
      illustration64,
      POLICY_DOC_TYPES.pdf,
      POLICY_DOC_SOURCES.app,
      primaryPolicy,
      POLICY_DOC_DOCTYPE.RevisedBasicIllustration,
      POLICY_DOC_DOCTYPE.RevisedBasicIllustration,
      null,
      'en',
      null,
      null,
      null
    );

    return newRBI;
  }

  static async deleteExistingRBIIfExists(policy, party, txn, ipAddress) {
    const existingRBIs = await PolicyDoc.persistorFetchByQuery({
      policyId: policy._id,
      docType: POLICY_DOC_DOCTYPE.RevisedBasicIllustration,
      isActive: 'true',
    });

    const revisedProps = await IllustrationRequestProperty.persistorFetchByQuery({
      policy_id: policy._id,
      illustrationType: 'revisedBasicIllustration',
    });

    if (existingRBIs.length === 1 && revisedProps.length > 0) {
      await PolicyDocAction.newWithProps(existingRBIs[0], party, POLICY_DOC_ACTION_TYPE.DELETED, ipAddress).persistSave(
        txn
      );

      existingRBIs[0].persistDelete({ transaction: txn });
      revisedProps.forEach(async (prop) => {
        await prop.persistDelete({ transaction: txn });
      });

      UILoggerSingleton.getInstance().info(
        {
          module: 'RevisedBasicIllustrationUtils',
          function: 'deleteExistingRBI',
          data: {
            policyNumber: policy.policyNumber,
            existingRBI: existingRBIs[0]._id,
          },
          category: UICategory.request,
        },
        `Policy had an existing RBI which is deleted`
      );
    }
  }

  static async generateRBIandReplaceIfOneAlreadyExists(primaryPolicy: Policy, party: Party | Admin, txn, ipAddress?) {
    const wholeLifePolicy = (primaryPolicy as CoverpathPolicy).application.primaryPolicyWithPendingAmendment;
    const currentRequest: IllustrationRequest = IllustrationService.createPolicyIllustrationRequestForRBI(
      primaryPolicy.primaryPolicyAgent.primaryAgent,
      wholeLifePolicy as CoverpathPolicy
    );

    const revisedProps = await IllustrationRequestProperty.persistorFetchByQuery({
      policy_id: primaryPolicy._id,
      illustrationType: 'revisedBasicIllustration',
    });
    RevisedBasicIllustrationUtils.addDefault1035PropsIfNeeded(revisedProps);
    const originalProps = await IllustrationRequestProperty.persistorFetchByQuery({
      policy_id: primaryPolicy._id,
      illustrationType: 'originalBasicIllustration',
    });
    RevisedBasicIllustrationUtils.addDefault1035PropsIfNeeded(originalProps);

    const currentProps = RevisedBasicIllustrationUtils.serializeIllustrationRequest(currentRequest);
    RevisedBasicIllustrationUtils.addDefault1035PropsIfNeeded(currentProps);
    const currentAndOriginalAreDifferent = RevisedBasicIllustrationUtils.shouldGenerateRBIBasedonParametersChange(
      currentProps,
      originalProps,
      primaryPolicy
    );

    if (!currentAndOriginalAreDifferent) {
      await RevisedBasicIllustrationUtils.deleteExistingRBIIfExists(primaryPolicy, party, txn, ipAddress);
      revisedProps.length = 0;
    }

    const revisedOrOriginalProps = revisedProps.length > 0 ? revisedProps : originalProps;
    const rbiNeeded = RevisedBasicIllustrationUtils.shouldGenerateRBIBasedonParametersChange(
      currentProps,
      revisedOrOriginalProps,
      primaryPolicy
    );

    if (!rbiNeeded) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'RevisedBasicIllustrationUtils',
          function: 'generateRBIandReplaceIfOneAlreadyExists',
          data: {
            policyNumber: primaryPolicy.policyNumber,
          },
          category: UICategory.request,
        },
        `New RBI not needed`
      );

      return;
    }

    //delete existing RBI
    await RevisedBasicIllustrationUtils.deleteExistingRBIIfExists(primaryPolicy, party, txn, ipAddress);

    //Generate a new RBI
    const newRBI: PolicyDoc = await RevisedBasicIllustrationUtils.generateNewRBI(primaryPolicy, currentRequest);
    newRBI.uploadedBy = 'generateRBIandReplaceiIfOneAlreadyExists';

    if (newRBI) {
      await PolicyDocAction.newWithProps(newRBI, party, POLICY_DOC_ACTION_TYPE.CREATED, ipAddress).persistorSave({
        transaction: txn,
      });

      await newRBI.persistorSave({ transaction: txn });
      await Promise.all(
        currentProps.map(async (prop) => {
          prop.policy = primaryPolicy;
          prop.illustrationType = 'revisedBasicIllustration';
          await prop.persistorSave({ transaction: txn });
        })
      );
      UILoggerSingleton.getInstance().info(
        {
          module: 'RevisedBasicIllustrationUtils',
          function: 'generateRBIandReplaceIfOneAlreadyExists',
          data: {
            policyNumber: primaryPolicy.policyNumber,
            newRBI: newRBI._id,
          },
          category: UICategory.request,
        },
        `A new RBI with amended values is created and parameters are persisted `
      );
    } else {
      throw Error(
        `Error creating a new RBI with amended values, revised basic illustration and parameters were not persisted`
      );
    }
  }

  static async isPolicyMecForRBI(policy: CoverpathPolicy): Promise<boolean> {
    if (!policy.isWholeLifePolicyWithPendingAmendment()) {
      return false;
    }
    const illustrationRequest: IllustrationRequest = IllustrationService.createPolicyIllustrationRequestForRBI(
      policy.primaryPolicyAgent.agent,
      policy
    );
    return HtmlPdfController.new().getMecDataFromProduct(illustrationRequest);
  }

  static async isPolicyMecForBI(policy: CoverpathPolicy, convertedFromSanderling?: boolean): Promise<boolean> {
    if (!policy.isAccelerated() && !policy.isWholeLifePolicy()) {
      return false;
    }

    if (policy.isAccelerated() && !policy.isWholeLifePolicyWithPendingAmendment()) {
      return false;
    }

    // create current request
    const currentIllustrationRequest: IllustrationRequest = IllustrationService.createPolicyIllustrationRequest(
      policy.primaryPolicyAgent.agent,
      policy,
      false,
      convertedFromSanderling
    );

    if (policy.isAccelerated()) {
      IllustrationService.updateIllustrationRequestWithAmendmentValues(currentIllustrationRequest, policy);
    }

    return HtmlPdfController.new().getMecDataFromProduct(currentIllustrationRequest);
  }

  /*
    Old DocuSign code didn't always add optional + default props related to 1035 Exchanges, so temporarily insert them
    here before doing any comparisons
   */

  static addDefault1035PropsIfNeeded(illustrationRequestProps: IllustrationRequestProperty[]) {
    const mecStatusFound = illustrationRequestProps.some((x) => x.property === 'mecStatus');
    if (!mecStatusFound) {
      const newProp = new IllustrationRequestProperty();
      newProp.property = 'mecStatus';
      newProp.stringValue = 'No';

      illustrationRequestProps.push(newProp);
    }

    const costBasisFound = illustrationRequestProps.some((x) => x.property === 'costBasis');
    if (!costBasisFound) {
      const newProp = new IllustrationRequestProperty();
      newProp.property = 'costBasis';
      newProp.numberValue = 0;

      illustrationRequestProps.push(newProp);
    }

    const cashValueTransferFound = illustrationRequestProps.some((x) => x.property === 'cashValueTransfer');
    if (!cashValueTransferFound) {
      const newProp = new IllustrationRequestProperty();
      newProp.property = 'cashValueTransfer';
      newProp.numberValue = 0;

      illustrationRequestProps.push(newProp);
    }

    const applyTowardsFound = illustrationRequestProps.some((x) => x.property === 'applyTowards');
    if (!applyTowardsFound) {
      const newProp = new IllustrationRequestProperty();
      newProp.property = 'applyTowards';
      newProp.stringValue = '';

      illustrationRequestProps.push(newProp);
    }
  }

  static isInsuranceAgeUnChanged(previousStartDate, currentStartDate, dob) {
    const previousInsuranceAge: number = CoverpathUtils.getInsuranceAge(dob, previousStartDate);
    const currentInsuranceAge: number = CoverpathUtils.getInsuranceAge(dob, currentStartDate);

    UILoggerSingleton.getInstance().info(
      {
        module: 'RevisedBasicIllustrationUtils',
        function: 'isInsuranceAgeUnChanged',
        data: {
          previousInsuranceAge,
          currentInsuranceAge,
        },
        category: UICategory.request,
      },
      'isInsuranceAgeUnChanged'
    );
    return currentInsuranceAge === previousInsuranceAge;
  }

  static isNewAsOfDateCausingDividendChange(previousStartDate, currentStartDate) {
    const DIVIDEND_CHANGE_DATE = new Date(2020, 10, 2, 0, 0, 0);
    const dividendChanging = previousStartDate < DIVIDEND_CHANGE_DATE && currentStartDate >= DIVIDEND_CHANGE_DATE;

    UILoggerSingleton.getInstance().info(
      {
        module: 'RevisedBasicIllustrationUtils',
        function: 'isNewAsOfDateCausingDividendChange',
        data: {
          previousStartDate,
          currentStartDate,
          dividendChanging,
        },
        category: UICategory.request,
      },
      'isNewAsOfDateCausingDividendChange'
    );
    return dividendChanging;
  }
}
